import React, { useEffect, useState } from "react";
import CreateButton from "../../components/CreateButton/CreateButton";
import CancelButton from "../../components/CancelButton/CancelButton";
import axios from "axios";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { createFoodWithImage } from "../../redux/slices/addNewFoodSlice";
import { DocumentUpload } from "iconsax-react";
import { ToastContainer } from "react-toastify";
import { CloseCircleFilled } from "@ant-design/icons";
import { AsyncPaginate } from "react-select-async-paginate";
import { fetchRestaurants, fetchRestaurantsSearch } from "../../redux/slices/allRestaurantsSlice";
import top from "../../assets/Status bar.png";
import heart from "../../assets/Vector.png";
import FoodPreview from "../../components/FoodPreview/FoodPreview";
import { useTranslation } from "react-i18next";

type Props = {};

interface Restaurant {
  id: number;
  name: string;
  phoneNumber: string;
  location: string;
}

interface Category {
  id: number;
  name: string;
}

const AddNewFood: React.FC = (props: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState<number | null>(
    null
  );
  const [selectedCategory, setSelectedCategory] = useState<number[]>([]);

  const { t } = useTranslation();

  const [foodName, setFoodName] = useState<string>("");
  const [price, setPrice] = useState<number | string>("");
  const [image, setImage] = useState<File | null>(null);

  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [imageName, setImageName] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const restaurantName = restaurants.find(r => r.id === selectedRestaurant)?.name || '';

  useEffect(() => {
    async function fetchData() {
      const token = localStorage.getItem("token");
      try {
        const categoriesResponse = await axios.get(
          "https://dev.api.jomuntu.com/api/v1/categories?size=100",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const restaurantResponse = await axios.get(
          "https://dev.api.jomuntu.com/api/v1/food-venues?size=100",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setCategories(categoriesResponse.data.content);
        setRestaurants(restaurantResponse.data.content);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
    fetchData();
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
  
    if (!image) {
      newErrors.image = t("error.imageRequired");
    }
  
    if (!selectedRestaurant) {
      newErrors.restaurant = t("error.restaurantRequired"); // new key
    }
  
    if (!foodName.trim()) {
      newErrors.foodName = t("error.foodNameRequired");
    }
  
    if (selectedCategory.length === 0) {
      newErrors.category = t("error.categoryRequired");
    }
  
    if (!price) {
      newErrors.price = t("error.priceRequired");
    } else if (Number(price) <= 0) {
      newErrors.price = t("error.priceMustBePositive");
    }
  
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const loadRestaurantsOptions = async (
    name: string,
    loadedOptions: any,
    { page }: any
  ) => {
    try {
      const response = await dispatch(
        fetchRestaurants({
          page,
          size: 10,
          name,
        })
      ).unwrap();

      const restaurantsData = response.content;

      const options = restaurantsData.map((restaurant: Restaurant) => ({
        label: restaurant.name,
        value: restaurant.id,
      }));

      return {
        options,
        hasMore: !response.last,
        additional: {
          page: page + 1,
        },
      };
    } catch (error) {
      console.error("Failed to load restaurants:", error);
      return {
        options: [],
        hasMore: false,
      };
    }
  };

  const loadCategoryOptions = async (
    search: string,
    loadedOptions: any,
    { page }: any
  ) => {
    const token = localStorage.getItem("token");
    const isSearching = search && search.length > 0;

    const endpoint = isSearching
      ? `https://dev.api.jomuntu.com/api/v1/categories/search-by-name`
      : `https://dev.api.jomuntu.com/api/v1/categories`;

    try {
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: isSearching ? { name: search } : { size: 10, page },
      });

      const categories = isSearching ? response.data : response.data.content;

      const options = categories.map((category: Category) => ({
        label: category.name,
        value: category.id,
      }));

      return {
        options,
        hasMore: !isSearching && !response.data.last,
        additional: {
          page: page + 1,
        },
      };
    } catch (error) {
      console.error("Failed to load categories:", error);
      return {
        options: [],
        hasMore: false,
      };
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedImage = e.target.files[0];
      setImage(selectedImage);
      setImageName(selectedImage.name);
      if (errors.image) {
        const newErrors = {...errors};
        delete newErrors.image;
        setErrors(newErrors);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const foodData = {
        foodName,
        price,
        venueId: selectedRestaurant,
        categoryIds: selectedCategory,
      };

      await dispatch(createFoodWithImage(foodData, image));

      setFoodName("");
      setPrice("");
      setSelectedRestaurant(null);
      setSelectedCategory([]);
      setImage(null);
      setLocation("");
      setPhoneNumber("");
      setErrors({});
    } catch (error) {
      console.error("Error creating food item:", error);
    }
  };

  const handleCancel = () => {
    setFoodName("");
    setPrice("");
    setSelectedRestaurant(null);
    setSelectedCategory([]);
    setImage(null);
    setLocation("");
    setPhoneNumber("");
    setErrors({});
  };

  // Inside your component:
  const selectedCategoryNames = categories
    .filter((cat) => selectedCategory.includes(cat.id))
    .map((cat) => cat.name);

  return (
    <div className="flex w-full">
      <ToastContainer />
      <div className="flex flex-col items-center flex-grow bg-white w-2/3 p-4 overflow-auto">
        <div className="w-full">
          <form onSubmit={handleSubmit}>
            <div className="pt-8">
              <h1 className="text-2xl font-medium">{t("addFood.title")}</h1>
              <p className="text-sm">{t("addFood.subtitle")}</p>
            </div>

            <div className="flex flex-col gap-1 pt-6">
              <label className="text-sm font-medium">
                {t("addFood.image")} <span className="text-red-600">*</span>
              </label>

              {!image ? (
                <label className="w-full border-2 h-[124px] rounded-md border-dashed border-borderColor flex flex-col justify-center items-center cursor-pointer hover:border-[#D10000]">
                  <div className="flex justify-center p-2 rounded-full bg-gray-50">
                    <DocumentUpload color="#D10000" />
                  </div>
                  <h1>
                    <span className="font-medium text-[#D10000]">
                      {t("addFood.clickToUpload")}
                    </span>{" "}
                    {t("addFood.orDrag")}
                  </h1>
                  <h1 className="text-sm">{t("addFood.maxSize")}</h1>
                  <input
                    type="file"
                    onChange={handleImageChange}
                    name="file_upload"
                    className="hidden"
                    id="image"
                  />
                </label>
              ) : (
                <div className="relative mt-2 px-4 py-3 rounded-md bg-gray-100 border border-gray-300 flex items-center justify-between max-w-[400px]">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-full bg-customOrange/10">
                      <DocumentUpload color="#D10000" />
                    </div>
                    <span className="text-sm text-gray-700 break-all">
                      {image.name}
                    </span>
                  </div>
                  <CloseCircleFilled
                    onClick={() => {
                      setImage(null);
                      setImageName("");
                    }}
                    className="text-red-500 text-lg cursor-pointer hover:text-red-600"
                  />
                </div>
              )}
              {errors.image && <p className="text-red-500 text-xs mt-1">{errors.image}</p>}
            </div>

            <div className="flex w-full gap-4 pt-6">
              <div className="flex flex-col w-4/12 gap-1">
                <label className="text-sm font-medium">
                  {t("addFood.restaurant")} <span className="text-red-600">*</span>
                </label>
                <AsyncPaginate
                  placeholder={t("addFood.selectRestaurant")}
                  value={
                    selectedRestaurant
                      ? [
                          {
                            label:
                              restaurants.find(
                                (restaurant) =>
                                  restaurant.id === selectedRestaurant
                              )?.name || "",
                            value: selectedRestaurant,
                          },
                        ]
                      : []
                  }
                  loadOptions={loadRestaurantsOptions}
                  additional={{ page: 0 }}
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      setSelectedRestaurant(selectedOption.value);
                      const selectedRestaurantDetails = restaurants.find(
                        (restaurant) => restaurant.id === selectedOption.value
                      );
                      if (selectedRestaurantDetails) {
                        setPhoneNumber(selectedRestaurantDetails.phoneNumber);
                        setLocation(selectedRestaurantDetails.location);
                      }
                      // Clear error when restaurant is selected
                      if (errors.restaurant) {
                        const newErrors = {...errors};
                        delete newErrors.restaurant;
                        setErrors(newErrors);
                      }
                    }
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "40px",
                      fontSize: "14px",
                      borderColor: errors.restaurant ? "#ef4444" : "#e5e7eb",
                      borderRadius: "0.375rem",
                    }),
                  }}
                />
                {errors.restaurant && <p className="text-red-500 text-xs mt-1">{errors.restaurant}</p>}
              </div>

              <div className="flex flex-col w-4/12 gap-1">
                <label className="text-sm font-medium">
                  {t("addFood.name")} <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={foodName}
                  onChange={(e) => {
                    setFoodName(e.target.value);
                    // Clear error when typing
                    if (errors.foodName) {
                      const newErrors = {...errors};
                      delete newErrors.foodName;
                      setErrors(newErrors);
                    }
                  }}
                  placeholder={t("addFood.namePlaceholder")}
                  className={`border h-[40px] rounded-md pl-4 text-sm focus:border-2 focus:outline-none leading-[40px] ${
                    errors.foodName ? "border-red-500" : ""
                  }`}
                />
                {errors.foodName && <p className="text-red-500 text-xs mt-1">{errors.foodName}</p>}
              </div>

              <div className="flex flex-col w-4/12 gap-1">
                <label className="text-sm font-medium">
                  {t("addFood.category")} <span className="text-red-600">*</span>
                </label>
                <AsyncPaginate
                  isMulti
                  placeholder="Select Category"
                  value={selectedCategory
                    .map((id) => {
                      const found = categories.find((c) => c.id === id);
                      return found
                        ? { label: found.name, value: found.id }
                        : null;
                    })
                    .filter(Boolean)}
                  loadOptions={loadCategoryOptions}
                  additional={{ page: 0 }}
                  onChange={(selectedOptions) => {
                    const selectedIds = selectedOptions.map(
                      (option: any) => option.value
                    );
                    setSelectedCategory(selectedIds);
                    // Clear error when category is selected
                    if (errors.category) {
                      const newErrors = {...errors};
                      delete newErrors.category;
                      setErrors(newErrors);
                    }
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "40px",
                      fontSize: "14px",
                      borderColor: errors.category ? "#ef4444" : "#e5e7eb",
                      borderRadius: "0.375rem",
                    }),
                  }}
                />
                {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category}</p>}
              </div>
            </div>

            <div className="pt-8">
              <div>
                <h1 className="text-2xl font-medium">{t("addFood.providerTitle")}</h1>
              </div>

              <div className="flex w-full gap-4 pt-4 ">
                <div className="flex flex-col w-4/12 gap-1">
                  <label className="text-sm font-medium">
                    {t("addFood.phone")} <span className="text-red-600">*</span>
                  </label>
                  <input
                    readOnly
                    type="string"
                    value={phoneNumber}
                    placeholder={t("addFood.phonePlaceholder")}
                    className="border h-[40px] rounded-md pl-4 text-sm focus:border-2 focus:outline-none leading-[44px]"
                  />
                </div>

                <div className="flex flex-col w-4/12 gap-1">
                  <label className="text-sm font-medium">
                    {t("addFood.price")} <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => {
                      setPrice(e.target.value);
                      // Clear error when typing
                      if (errors.price) {
                        const newErrors = {...errors};
                        delete newErrors.price;
                        setErrors(newErrors);
                      }
                    }}
                    placeholder={t("addFood.price")}
                    className={`border h-[40px] rounded-md pl-4 text-sm focus:border-2 focus:outline-none leading-[44px] ${
                      errors.price ? "border-red-500" : ""
                    }`}
                  />
                  {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
                </div>

                <div className="flex flex-col w-4/12 gap-1">
                  <label className="text-sm font-medium">
                    {t("addFood.location")} <span className="text-red-600">*</span>
                  </label>
                  <input
                    readOnly
                    type="text"
                    value={location}
                    placeholder={t("addFood.location")}
                    className="border h-[40px] rounded-md pl-4 text-sm focus:border-2 focus:outline-none leading-[44px]"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-4 mt-28 mb-10">
                <CreateButton />
                <CancelButton onClick={handleCancel} />
              </div>
            </div>
          </form>
        </div>
      </div>
      <FoodPreview
        image={image}
        foodName={foodName}
        category={selectedCategoryNames}
        price={price}
        phoneNumber={phoneNumber}
        location={location}
        restaurantName={restaurantName}
        top={top}
        heart={heart}
      />
    </div>
  );
};

export default AddNewFood;